import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ProposedDiffText } from './DiffViews';
import { editorHtmlToRecipeText, isFieldBold, recipeTextToEditorHtml, toggleFieldBold } from './recipeTextFormat';

const CHECK_GREEN = '#18864a';

function ColumnLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        mb: 0.75,
        color: CHECK_GREEN,
      }}
    >
      {children}
    </Typography>
  );
}

function ProposedVisualEditor({
  value,
  multiline,
  onChange,
  onBoldState,
  boldNonce,
}: {
  value: string;
  multiline?: boolean;
  onChange: (value: string) => void;
  onBoldState: (active: boolean) => void;
  boldNonce: number;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
      }),
    ],
    content: recipeTextToEditorHtml(value),
    onUpdate: ({ editor: next }) => {
      onChange(editorHtmlToRecipeText(next.getHTML()));
      onBoldState(next.isActive('bold'));
    },
  }, []);

  useEffect(() => {
    if (!editor) return;
    const sync = () => onBoldState(editor.isActive('bold'));
    editor.on('selectionUpdate', sync);
    editor.on('transaction', sync);
    sync();
    return () => {
      editor.off('selectionUpdate', sync);
      editor.off('transaction', sync);
    };
  }, [editor, onBoldState]);

  useEffect(() => {
    if (!editor || boldNonce < 1) return;
    editor.chain().focus().toggleBold().run();
  }, [boldNonce, editor]);

  if (!editor) return null;

  return (
    <Box
      sx={{
        minHeight: multiline ? 96 : 44,
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        bgcolor: '#fff',
        border: '1px solid rgba(21,115,71,0.35)',
        '& .ProseMirror': {
          outline: 'none',
          fontSize: '0.95rem',
          lineHeight: 1.7,
          color: '#1c2b3a',
          '& p': { margin: 0 },
          '& strong': { fontWeight: 700 },
        },
      }}
    >
      <EditorContent editor={editor} />
    </Box>
  );
}

export default function ProposedField({
  originalValue,
  proposedValue,
  onEdit,
  multiline,
  readOnly,
  editing,
  onToggleEditing,
}: {
  originalValue: string;
  proposedValue: string;
  onEdit: (value: string) => void;
  multiline?: boolean;
  readOnly: boolean;
  editing: boolean;
  onToggleEditing: () => void;
}) {
  const [boldActive, setBoldActive] = useState(isFieldBold(proposedValue));
  const [boldNonce, setBoldNonce] = useState(0);

  useEffect(() => {
    if (!editing) setBoldActive(isFieldBold(proposedValue));
  }, [editing, proposedValue]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <ColumnLabel>Förslag</ColumnLabel>
        {!readOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={boldActive ? 'Ta bort fetstil' : 'Fetstil'}>
              <IconButton
                size="small"
                onClick={() => {
                  if (editing) {
                    setBoldNonce((value) => value + 1);
                    return;
                  }
                  onEdit(toggleFieldBold(proposedValue));
                }}
                aria-label="Fetstil"
                sx={{ color: boldActive ? CHECK_GREEN : '#64748b' }}
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={editing ? 'Stäng redigering' : 'Justera förslaget'}>
              <IconButton size="small" onClick={onToggleEditing} aria-label={editing ? 'Stäng redigering' : 'Justera förslaget'}>
                {editing ? <CloseIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {editing && !readOnly ? (
        <ProposedVisualEditor
          value={proposedValue}
          multiline={multiline}
          onChange={onEdit}
          onBoldState={setBoldActive}
          boldNonce={boldNonce}
        />
      ) : (
        <Box
          onClick={readOnly ? undefined : onToggleEditing}
          sx={{
            cursor: readOnly ? 'default' : 'text',
            minHeight: 44,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            bgcolor: '#f4fbf6',
            border: '1px solid rgba(21,115,71,0.18)',
          }}
        >
          <ProposedDiffText original={originalValue} proposed={proposedValue} />
        </Box>
      )}
    </Box>
  );
}
